<template>
	<NuxtLayout name="default">
		<template #hero>
			<HomeHero
				show-search
				:content="content || {}"
			/>
		</template>
		<div class="pb-16 m-auto">
			<div class="max-w-6xl pt-8 pb-12 mx-4 border-b lg:mx-auto md:mx-8">
				<LearnMore
					v-for="(learnMore, index) in learnMoreSections"
					:key="index"
					:data="learnMore"
					:position="getPosition('right', index)"
				/>
			</div>
			<div class="flex flex-col items-center max-w-6xl pb-12 lg:mx-auto lg:flex-row lg:pb-0">
				<ServiceAreasChecklist
					class="lg:w-1/2 md:mx-16"
					:checklist-data="checklistData"
				/>
				<ServiceAreasCheckAvailability :location="location" />
			</div>
			<ServiceAreasContentBlock
				v-if="contentBlockBanner"
				:title="contentBlockBanner?.title"
				:content="contentBlockBanner?.subtitle"
			/>
			<Faq
				:faqs="faqs"
				faqs-from-story-blok
				class="max-w-6xl px-8 pt-12 border-t-2 lg:px-0 lg:mx-auto md:mx-8"
			/>
			<CarouselTestimonial class="my-12" />
			<ServiceAreasFacts
				:facts="facts"
				:title="factsTitle"
				class="max-w-6xl mx-auto"
				:city="city"
			/>
		</div>
	</NuxtLayout>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import type { HeroContentProps } from '@/components/home/types'
import {
	STORYBLOK_VERSION,
	COMPANY_NAME,
	COMPANY_PHONE,
	BASE_URL
} from '@/constants/index.js'
import {
	JSON_LD_LOGO,
	JSON_LD_HAS_OFFER_CATALOG
} from '@/constants/json-ld'
import type { ChecklistProps } from '@/components/serviceAreas/types'
import { mapLearnMore, getPosition } from '@/utils/learnMoreMap'
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'

const { getStory } = useStoryblokWithErrorReporting()

const route = useRoute()
const { locale } = useI18n({
	useScope: 'global'
})

const story = await getStory(`service-areas/${route?.params?.area}`, {
	version: STORYBLOK_VERSION,
	language: locale.value
})

const { title, description } = story?.value?.content?.metatags ?? { title: '', description: '' }

const contentBlockBanner = computed(() => {
	return story.value?.content?.bannerContent?.[0]
})
const facts = computed(() => {
	return story.value?.content?.fastFactsContent?.[0]?.list?.map((obj:any) => obj.value)
})
const factsTitle = computed(() => {
	return story.value?.content?.fastFactsContent?.[0]?.title
})
const connectCta = computed(() => {
	return story.value?.content?.connectCtaContent?.[0]
})
const ctaList = computed(() => {
	return connectCta?.value?.list?.map((obj:any) => obj.value)
})
const faqs = computed(() => {
	return story.value?.content?.faqContent
})

const location = computed(() => {
	return connectCta.value?.location
})

const learnMoreSections = mapLearnMore(story.value?.content?.learnMoreContent)

const cityCardContent = computed(() => {
	return story.value?.content?.cityCardContent?.[0]
})

const cityImage = computed(() => {
	return cityCardContent.value?.image.filename
})

const city = computed(() => {
	if (typeof route?.params?.area === 'string') {
		return route?.params?.area?.split('-')[0] || ''
	}
	return ''
})

const cityName = computed(() => {
	return cityCardContent.value?.city
})

const stateShort = computed(() => {
	return cityCardContent.value?.stateShort
})

const getTitle = computed(() => {
	const header = story.value?.content?.headerContent[0]
	return {
		title: header?.title,
		subtitle: header?.subtitle,
		description: header?.searchCTA
	}
})
const content = computed(() => { // content for hero set in route meta
	if (getTitle.value) {
		return getTitle.value as HeroContentProps
	} else if (Object.keys(route?.meta).length) {
		return route?.meta as HeroContentProps
	} else {
		reportError(new Error('Default Layout - No content for hero set in route meta'))
		return {}
	}
})

const checklistData = computed<ChecklistProps>(() => {
	return {
		location: location.value,
		title: connectCta.value?.title,
		subtitle: connectCta.value?.subtitle,
		description: connectCta.value?.description,
		list: ctaList.value
	}
})

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogImage: () => cityImage.value,
	ogUrl: () => `${BASE_URL}${route?.fullPath}`,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => cityImage.value,
	twitterCard: () => 'summary_large_image'
})

useHead({
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicon.ico'
		}
	]
})

definePageMeta({
	layout: false,
	showAddressSearch: true,
	isAreaPage: true
})

useJsonld({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: BASE_URL
				}, {
					'@type': 'ListItem',
					position: 2,
					name: 'Service Areas',
					item: `${BASE_URL}/service-areas`
				}, {
					'@type': 'ListItem',
					position: 3,
					name: city.value,
					item: `${BASE_URL}${route?.fullPath}`
				}
			]
		},
		{
			'@type': 'Service',
			name: COMPANY_NAME,
			logo: JSON_LD_LOGO,
			provider: {
				'@type': 'Organization',
				name: COMPANY_NAME,
				telephone: COMPANY_PHONE
			},
			serviceType: 'High-speed Internet',
			serviceOutput: 'Internet service',
			areaServed: [
				{
					'@type': 'Place',
					address: {
						'@type': 'PostalAddress',
						addressLocality: cityName.value,
						addressRegion: stateShort.value
					},
					potentialAction: {
						'@type': 'SearchAction',
						query: `Internet services in ${cityName.value}, ${stateShort.value}`
					}
				}
			],
			hasOfferCatalog: JSON_LD_HAS_OFFER_CATALOG
		}
	]
})
</script>
